/**
 * CycloneSpec Validator
 * 
 * Validates blueprint content against the CycloneSpec v0 schema.
 * Uses manual validation to avoid CSP 'unsafe-eval' requirement.
 * This is more secure than using ajv.compile() which requires eval().
 */

import { logger } from '../services/loggingService';

/**
 * Manual schema validation (CSP-safe, no eval required)
 */
function validateSchema(data: any): { valid: boolean; errors: any[] } {
  const errors: any[] = [];

  // Required: project_name
  if (!data.project_name || typeof data.project_name !== 'string') {
    errors.push({
      keyword: 'required',
      dataPath: '/project_name',
      schemaPath: '#/required',
      params: { missingProperty: 'project_name' },
      message: 'should have required property \'project_name\'',
    });
  }

  // Validate optional arrays
  const arrayFields = ['goals', 'data_contracts', 'apis', 'policies', 'tests'];
  arrayFields.forEach(field => {
    if (data[field] !== undefined && !Array.isArray(data[field])) {
      errors.push({
        keyword: 'type',
        dataPath: `/${field}`,
        schemaPath: `#/properties/${field}/type`,
        params: { type: 'array' },
        message: `should be array`,
      });
    }
  });

  // Validate deploy object
  if (data.deploy !== undefined && typeof data.deploy !== 'object') {
    errors.push({
      keyword: 'type',
      dataPath: '/deploy',
      schemaPath: '#/properties/deploy/type',
      params: { type: 'object' },
      message: 'should be object',
    });
  }

  // Check for additional properties (if strict mode)
  const allowedProps = [
    'project_name', 'version', 'goals', 'data_contracts', 
    'apis', 'policies', 'tests', 'deploy', 'metadata'
  ];
  Object.keys(data).forEach(key => {
    if (!allowedProps.includes(key)) {
      errors.push({
        keyword: 'additionalProperties',
        dataPath: '',
        schemaPath: '#/additionalProperties',
        params: { additionalProperty: key },
        message: `should NOT have additional properties`,
      });
    }
  });

  return {
    valid: errors.length === 0,
    errors: errors.length > 0 ? errors : null as any,
  };
}

/**
 * Validate blueprint content against CycloneSpec
 */
export interface ValidationResult {
  valid: boolean;
  errors: Array<{
    message: string;
    dataPath: string;
    schemaPath: string;
    params: any;
    severity: 'error' | 'warning';
  }>;
  warnings: string[];
  missingSections: string[];
  coverage: {
    goals: number;
    dataContracts: number;
    apis: number;
    policies: number;
    tests: number;
    deploy: boolean;
  };
  suggestions: string[];
}

/**
 * Main validation function
 */
export async function validateBlueprintContent(content: any): Promise<ValidationResult> {
  const result: ValidationResult = {
    valid: true,
    errors: [],
    warnings: [],
    missingSections: [],
    coverage: {
      goals: 0,
      dataContracts: 0,
      apis: 0,
      policies: 0,
      tests: 0,
      deploy: false,
    },
    suggestions: [],
  };

  try {
    // Use manual validation (CSP-safe)
    const validation = validateSchema(content);
    
    if (!validation.valid) {
      // Collect all errors
      const errors = validation.errors?.map(err => {
        const severity = err.keyword === 'required' ? 'error' : 'warning';
        return {
          message: err.message || `Validation failed at ${err.dataPath}`,
          dataPath: err.dataPath,
          schemaPath: err.schemaPath,
          params: err.params,
          severity,
        };
      }) || [];

      result.valid = false;
      result.errors = errors;

      // Add warnings for missing required sections
      const requiredSections = ['goals', 'data_contracts', 'apis', 'policies', 'tests', 'deploy'];
      const missing = requiredSections.filter(section => 
        !content.hasOwnProperty(section) || !content[section]?.length
      );

      if (missing.length > 0) {
        result.missingSections = missing;
        result.warnings.push(
          `Missing required sections: ${missing.join(', ')}. These must be defined for production deployments.`
        );
      }

      logger.warn('Blueprint validation failed', {
        errors: errors.length,
        missingSections: missing.length,
        contentKeys: Object.keys(content),
      });
    }

    // Coverage analysis
    result.coverage.goals = content.goals?.length || 0;
    result.coverage.dataContracts = content.data_contracts?.length || 0;
    result.coverage.apis = content.apis?.length || 0;
    result.coverage.policies = content.policies?.length || 0;
    result.coverage.tests = content.tests?.length || 0;
    result.coverage.deploy = !!content.deploy;

    // Generate suggestions based on coverage
    if (result.coverage.goals < 3) {
      result.suggestions.push('Define at least 3 business goals with measurable success criteria.');
    }

    if (result.coverage.dataContracts === 0) {
      result.suggestions.push('Define data contracts for all entities (User, Product, etc.) with PII classification.');
    }

    if (result.coverage.apis < 5) {
      result.suggestions.push('Define API endpoints with input/output schemas and security requirements.');
    }

    if (result.coverage.tests === 0) {
      result.suggestions.push('Add test requirements for unit, integration, and e2e coverage (target 80% minimum).');
    }

    if (!result.coverage.deploy) {
      result.suggestions.push('Configure deployment strategy (staging/production) and target providers (Vercel/AWS).');
    }

    // Warn on missing critical fields
    if (!content.project_name) {
      result.errors.push({
        message: 'Project name is required for traceability and deployment.',
        dataPath: '/project_name',
        schemaPath: '#/required',
        params: { missing: 'project_name' },
        severity: 'error',
      });
    }

    // Check for common anti-patterns
    if (content.apis && content.apis.some((api: any) => !api.auth_required)) {
      result.warnings.push('All APIs should require authentication by default. Review auth_required: false endpoints.');
    }

    if (content.data_contracts && content.data_contracts.some((contract: any) => 
      contract.pii_classification === 'critical' && !contract.retention_policy
    )) {
      result.warnings.push('Critical PII data contracts must define retention policies and geo-residency.');
    }

  } catch (error) {
    logger.error('Error during blueprint validation', { error });
    result.valid = false;
    result.errors.push({
      message: 'Validation process failed',
      dataPath: '',
      schemaPath: '',
      params: { error: error instanceof Error ? error.message : String(error) },
      severity: 'error',
    });
  }

  return result;
}

/**
 * Synchronous validation (for UI feedback)
 */
export function validateBlueprintContentSync(content: any): ValidationResult {
  // Simplified sync version - use for instant feedback
  // Full async validation should be used for production
  const result: ValidationResult = {
    valid: true,
    errors: [],
    warnings: [],
    missingSections: [],
    coverage: {
      goals: content.goals?.length || 0,
      dataContracts: content.data_contracts?.length || 0,
      apis: content.apis?.length || 0,
      policies: content.policies?.length || 0,
      tests: content.tests?.length || 0,
      deploy: !!content.deploy,
    },
    suggestions: [],
  };

  // Basic structural checks
  if (!content.project_name) {
    result.valid = false;
    result.errors.push({
      message: 'Project name is required',
      dataPath: '/project_name',
      schemaPath: '#/required',
      params: { missing: 'project_name' },
      severity: 'error',
    });
  }

  const requiredSections = ['goals', 'data_contracts', 'apis', 'policies', 'tests', 'deploy'];
  const missing = requiredSections.filter(section => 
    !content.hasOwnProperty(section) || !content[section]?.length
  );

  if (missing.length > 0) {
    result.valid = false;
    result.missingSections = missing;
    result.warnings.push(`Missing sections: ${missing.join(', ')}`);
  }

  // Quick coverage suggestions
  if (result.coverage.goals === 0) {
    result.suggestions.push('Add business goals to guide implementation.');
  }

  return result;
}

/**
 * Validate and sanitize blueprint content before saving
 * Returns sanitized content or throws on critical errors
 */
export function validateAndSanitize(content: any): any {
  const validation = validateBlueprintContentSync(content);
  
  if (!validation.valid) {
    const criticalErrors = validation.errors.filter(e => e.severity === 'error');
    if (criticalErrors.length > 0) {
      const errorMessages = criticalErrors.map(e => e.message).join('; ');
      throw new Error(`Validation failed: ${errorMessages}`);
    }
  }

  // Sanitize: remove undefined/null, ensure required fields have defaults
  const sanitized = { ...content };

  // Ensure required top-level properties
  if (!sanitized.project_name) {
    sanitized.project_name = 'Untitled Project';
  }

  if (!sanitized.version) {
    sanitized.version = 'v0.1';
  }

  // Ensure arrays are initialized
  ['goals', 'data_contracts', 'apis', 'policies', 'tests'].forEach(section => {
    if (!sanitized[section]) {
      sanitized[section] = [];
    }
  });

  if (!sanitized.deploy) {
    sanitized.deploy = {
      environment: 'staging',
      strategy: 'canary',
      providers: ['vercel'],
      auto_promote: false,
      rollback_enabled: true,
    };
  }

  // Remove empty objects/arrays for cleaner storage
  Object.keys(sanitized).forEach(key => {
    if (sanitized[key] === null || sanitized[key] === undefined) {
      delete sanitized[key];
    } else if (Array.isArray(sanitized[key]) && sanitized[key].length === 0) {
      delete sanitized[key];
    } else if (typeof sanitized[key] === 'object' && Object.keys(sanitized[key]).length === 0) {
      delete sanitized[key];
    }
  });

  return sanitized;
}

/**
 * Get validation suggestions for incomplete blueprints
 */
export function getValidationSuggestions(content: any): string[] {
  const validation = validateBlueprintContentSync(content);
  const suggestions: string[] = [];

  if (validation.coverage.goals < 3) {
    suggestions.push('Define 3+ business goals with success criteria.');
  }

  if (validation.coverage.dataContracts === 0) {
    suggestions.push('Create data contracts for main entities (User, Order, etc.)');
  }

  if (validation.coverage.apis === 0) {
    suggestions.push('Specify API endpoints with input/output validation schemas');
  }

  if (validation.coverage.tests === 0) {
    suggestions.push('Add test requirements targeting 80%+ coverage');
  }

  if (!validation.coverage.deploy) {
    suggestions.push('Configure deployment target and strategy');
  }

  // Security suggestions
  if (content.apis?.some((api: any) => !api.auth_required)) {
    suggestions.push('Review APIs with auth_required: false - consider adding authentication');
  }

  return suggestions;
}

// Export validation functions
export default { validateBlueprintContent, validateBlueprintContentSync, validateAndSanitize, getValidationSuggestions };
